<template>
  <div v-for="contract in contracts" :key="contract.contractId" class="contract-card mb-4">
    <div class="contract-card-header d-flex flex-column flex-md-row justify-content-between align-items-md-center">
      <div class="d-flex align-items-center mb-2 mb-md-0">
        <div class="contract-card-icon me-3">
          <i class="fa-solid fa-file-contract"></i>
        </div>
        <div>
          <div class="contract-card-title">{{ contract.contractTypeLabel }}</div>
          <small class="text-muted">vom {{ contract.createdAt }}</small>
        </div>
      </div>
      <div class="d-flex flex-column flex-md-row align-items-md-center">
        <span class="contract-status" :class="statusBadge(contract).class">{{ statusBadge(contract).label }}</span>
        <!-- HR only, a completed contract can't be deleted -->
        <button v-if="isBsgSide && !(contract.signedByUser && contract.signedByBsg)"
                type="button"
                class="btn btn-outline-danger fw-bold px-3 d-flex justify-content-center ms-md-3 mt-2 mt-md-0 w-100 w-md-auto"
                @click="emit('delete', contract.contractId)">
          <i class="fa-solid fa-trash me-1"></i> Löschen
        </button>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table contract-table mb-0">
        <thead>
        <tr>
          <th>Dokument</th>
          <th class="text-center">{{ isBsgSide ? 'Mitarbeiter' : 'Ihre Unterschriften' }}</th>
          <th class="text-center">BSG</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="document in contract.documents" :key="document.id">
          <td class="fw-bold text-dark">
            <i class="fa-regular fa-file-lines text-muted me-2"></i>{{ document.documentTypeLabel }}
          </td>
          <td class="text-center">
            <span class="signature-pill" :class="document.signedByUser ? 'is-signed' : 'is-pending'">
              <i class="fa-solid me-1" :class="document.signedByUser ? 'fa-check' : 'fa-clock'"></i>
              {{ document.signaturesByUser }} / {{ document.requiredSignaturesByUser }}
            </span>
          </td>
          <td class="text-center">
            <span class="signature-pill" :class="document.signedByBsg ? 'is-signed' : 'is-pending'">
              <i class="fa-solid me-1" :class="document.signedByBsg ? 'fa-check' : 'fa-clock'"></i>
              {{ document.signaturesByBsg }} / {{ document.requiredSignaturesByBsg }}
            </span>
          </td>
          <td class="text-end text-nowrap">
            <!-- PDF exists only when both sides signed the document -->
            <button v-if="document.signedByUser && document.signedByBsg"
                    type="button"
                    class="btn btn-light border fw-bold px-3 me-2 d-inline-flex align-items-center justify-content-center btn-sm contract-open-button"
                    title="PDF anzeigen"
                    @click="userContractService.showDocumentPdf(document.id)">
              <img src="@/assets/img/pdf_icon_version4.png" alt="PDF" style="width: 16px; height: auto;" class="me-1"/>
              <span style="font-size: 12px;">PDF</span>
            </button>
            <button type="button"
                    class="btn btn-sm fw-bold px-3 d-inline-flex align-items-center contract-open-button"
                    :class="canSign(document) ? 'btn-primary' : 'btn-outline-primary'"
                    @click="emit('open', document.id)">
              <i class="fa-solid me-2" :class="canSign(document) ? 'fa-signature' : 'fa-eye'"></i>
              {{ canSign(document) ? 'Öffnen und unterschreiben' : 'Ansehen' }}
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, defineEmits, defineProps} from 'vue';
import userContractService from '@/api/UserContract';
import {piniaCompanySettingsStore} from '@/store/piniaCompanySettingsStore';

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const companyColor = computed(() => companySettings.value?.colorCode || '#15803d');
const companyBgColor = computed(() => companySettings.value?.colorCode
    ? `${companySettings.value.colorCode}15`
    : '#ecfdf5'
);

// isBsgSide: HR view (signs for BSG), otherwise the external user's own view
const props = defineProps<{ contracts: any[], isBsgSide: boolean }>();
const emit = defineEmits<{
  (e: 'open', documentId: number): void,
  (e: 'delete', contractId: number): void,
}>();

function canSign(document: any): boolean {
  return props.isBsgSide ? !document.signedByBsg : !document.signedByUser;
}

function statusBadge(contract: any) {
  if (contract.signedByUser && contract.signedByBsg) {
    return {label: 'Vollständig unterschrieben', class: 'is-success'};
  }
  if (contract.signedByUser) {
    return {label: 'Wartet auf Unterschrift der BSG', class: 'is-info'};
  }
  if (contract.signedByBsg) {
    return {label: 'Wartet auf Unterschrift des Mitarbeiters', class: 'is-warning'};
  }
  return {label: props.isBsgSide ? 'Nicht unterschrieben' : 'Ihre Unterschrift fehlt', class: 'is-warning'};
}
</script>

<style scoped>
.contract-card {
  background-color: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
  overflow: hidden;
}

.contract-card-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eef1f5;
}

.contract-card-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: v-bind(companyBgColor);
  color: v-bind(companyColor);
  font-size: 18px;
}

.contract-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
}

.contract-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.contract-status.is-success {
  background-color: #ecfdf5;
  color: #15803d;
}

.contract-status.is-info {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.contract-status.is-warning {
  background-color: #fffbeb;
  color: #b45309;
}

.contract-table th {
  background-color: #f9fafb;
  border-top: none;
  border-bottom: 1px solid #eef1f5;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 10px 20px;
}

.contract-table td {
  border-top: 1px solid #f1f3f6;
  padding: 12px 20px;
  vertical-align: middle;
}

.contract-table tbody tr:first-child td {
  border-top: none;
}

.contract-table tbody tr {
  transition: background-color 0.15s ease-in-out;
}

.contract-table tbody tr:hover {
  background-color: #fafbfc;
}

.signature-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.signature-pill.is-signed {
  background-color: #ecfdf5;
  color: #15803d;
}

.signature-pill.is-pending {
  background-color: #f3f4f6;
  color: #6b7280;
}

.contract-open-button {
  min-height: 34px;
}
</style>
